import React from 'react';
import {ScrollView, StyleSheet, View} from 'react-native';
import {ThemeProvider, useTheme} from '@rneui/themed';
import type {Meta, StoryObj} from '@storybook/react-native';

import config from '../../config';
import rewardsTheme from '../../theme/rewardsTheme';

import Text from './Text';

// Component to display current brand theme
function BrandThemeShowcase() {
  const {theme} = useTheme();

  return (
    <ScrollView style={styles.scrollView}>
      <View style={styles.container}>
        {/* Brand Header */}
        <View style={styles.section}>
          <Text variant="heading0" style={styles.brandName}>
            {config.APP_NAME || 'Brand Name'}
          </Text>
          <Text variant="body2" style={styles.subtitle}>
            Theme Showcase
          </Text>
        </View>

        {/* Primary Brand Colors */}
        <View style={styles.section}>
          <Text variant="heading2" style={styles.sectionTitle}>
            Primary Brand Colors
          </Text>
          <View style={styles.colorGrid}>
            <ColorSwatch
              name="Primary"
              color={theme.colors.legacy.general.primary}
              description="Main brand color"
            />
            <ColorSwatch
              name="Secondary"
              color={theme.colors.legacy.general.secondary}
              description="Secondary brand color"
            />
            <ColorSwatch
              name="Highlight"
              color={theme.colors.legacy.general.highlight}
              description="Accent color"
            />
          </View>
        </View>

        {/* UI Control Colors */}
        <View style={styles.section}>
          <Text variant="heading2" style={styles.sectionTitle}>
            UI Controls
          </Text>
          <View style={styles.colorGrid}>
            <ColorSwatch
              name="Primary Button"
              color={theme.colors.legacy.uiControls.primary}
              description="Primary button color"
            />
            <ColorSwatch
              name="Secondary Button"
              color={theme.colors.legacy.uiControls.secondary}
              description="Secondary button color"
            />
          </View>
        </View>

        {/* Text Colors */}
        <View style={styles.section}>
          <Text variant="heading2" style={styles.sectionTitle}>
            Text Colors
          </Text>
          <View style={styles.colorGrid}>
            <ColorSwatch
              name="Heading"
              color={theme.colors.legacy.text.heading.primary}
              description="Heading text color"
            />
            <ColorSwatch
              name="Content"
              color={theme.colors.legacy.text.content.primary}
              description="Body text color"
            />
            <ColorSwatch
              name="Link"
              color={theme.colors.legacy.text.link.primary}
              description="Link text color"
            />
          </View>
        </View>

        {/* Background Colors */}
        <View style={styles.section}>
          <Text variant="heading2" style={styles.sectionTitle}>
            Background Colors
          </Text>
          <View style={styles.colorGrid}>
            <ColorSwatch
              name="Primary BG"
              color={theme.colors.legacy.background.general.primary}
              description="Primary background"
            />
            <ColorSwatch
              name="Button BG"
              color={theme.colors.legacy.background.button.primary}
              description="Button background"
            />
          </View>
        </View>

        {/* Grey Scale */}
        <View style={styles.section}>
          <Text variant="heading2" style={styles.sectionTitle}>
            Grey Scale
          </Text>
          <View style={styles.colorGrid}>
            <ColorSwatch name="Grey 0" color={theme.colors.grey0} description="Darkest grey" />
            <ColorSwatch name="Grey 1" color={theme.colors.grey1} description="Dark grey" />
            <ColorSwatch name="Grey 2" color={theme.colors.grey2} description="Medium grey" />
            <ColorSwatch name="Grey 3" color={theme.colors.grey3} description="Light grey" />
            <ColorSwatch name="Grey 5" color={theme.colors.grey5} description="Lightest grey" />
            <ColorSwatch
              name="Grey Outline"
              color={theme.colors.greyOutline}
              description="Border color"
            />
          </View>
        </View>

        {/* Typography Sizes */}
        <View style={styles.section}>
          <Text variant="heading2" style={styles.sectionTitle}>
            Typography Scale
          </Text>
          <View style={styles.typographyList}>
            <TypeSample variant="heading0" label="Heading 0" />
            <TypeSample variant="heading1" label="Heading 1" />
            <TypeSample variant="heading2" label="Heading 2" />
            <TypeSample variant="heading3" label="Heading 3" />
            <TypeSample variant="heading4" label="Heading 4" />
            <TypeSample variant="body" label="Body" />
            <TypeSample variant="body2" label="Body 2" />
            <TypeSample variant="caption" label="Caption" />
          </View>
        </View>

        {/* Brand Info */}
        <View style={styles.section}>
          <Text variant="heading2" style={styles.sectionTitle}>
            Brand Configuration
          </Text>
          <View style={styles.infoBox}>
            <InfoRow label="App Name" value={config.APP_NAME} />
            <InfoRow label="Base Tenant" value={config.BASE_TENANT} />
            <InfoRow
              label="Environment"
              value={
                config.API_GRAPHQL_URL?.includes('localhost') ? 'Local Development' : 'Production'
              }
            />
          </View>
        </View>
      </View>
    </ScrollView>
  );
}

// Helper Components
function ColorSwatch({
  name,
  color,
  description,
}: {
  name: string;
  color: string;
  description?: string;
}) {
  const isLight = color === '#FFFFFF' || color.toLowerCase().includes('fff');
  return (
    <View style={styles.colorSwatchContainer}>
      <View
        style={[styles.colorSwatch, {backgroundColor: color}, isLight && styles.colorSwatchBorder]}
      />
      <Text variant="body2" style={styles.colorName}>
        {name}
      </Text>
      <Text variant="caption" style={styles.colorValue}>
        {color}
      </Text>
      {description ? (
        <Text variant="caption" style={styles.colorDescription}>
          {description}
        </Text>
      ) : null}
    </View>
  );
}

function TypeSample({variant, label}: {variant: any; label: string}) {
  const {theme} = useTheme();
  const fontSize = theme.size.text[variant] || 16;
  return (
    <View style={styles.typeSampleContainer}>
      <Text variant={variant}>{label}</Text>
      <Text variant="caption" style={styles.typeSampleInfo}>
        {fontSize}px
      </Text>
    </View>
  );
}

function InfoRow({label, value}: {label: string; value: string}) {
  return (
    <View style={styles.infoRow}>
      <Text variant="body2" style={styles.infoLabel}>
        {label}:
      </Text>
      <Text variant="body2" style={styles.infoValue}>
        {value}
      </Text>
    </View>
  );
}

const meta = {
  title: 'Foundations/Brand Theme',
  component: BrandThemeShowcase,
  decorators: [
    Story => (
      <ThemeProvider theme={rewardsTheme}>
        <Story />
      </ThemeProvider>
    ),
  ],
} satisfies Meta<typeof BrandThemeShowcase>;

export default meta;

type Story = StoryObj<typeof meta>;

export const Default: Story = {
  render: () => <BrandThemeShowcase />,
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  container: {
    padding: 16,
  },
  section: {
    marginBottom: 32,
  },
  brandName: {
    marginBottom: 8,
  },
  subtitle: {
    opacity: 0.7,
  },
  sectionTitle: {
    marginBottom: 16,
  },
  colorGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  colorSwatchContainer: {
    width: 100,
    alignItems: 'center',
  },
  colorSwatch: {
    width: 80,
    height: 80,
    borderRadius: 8,
    marginBottom: 8,
  },
  colorSwatchBorder: {
    borderWidth: 1,
    borderColor: '#ccc',
  },
  colorName: {
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 4,
  },
  colorValue: {
    fontSize: 10,
    textAlign: 'center',
    marginBottom: 2,
  },
  colorDescription: {
    fontSize: 9,
    textAlign: 'center',
    opacity: 0.6,
  },
  typographyList: {
    gap: 12,
  },
  typeSampleContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  typeSampleInfo: {
    opacity: 0.5,
  },
  infoBox: {
    backgroundColor: '#f9f9f9',
    padding: 16,
    borderRadius: 8,
    gap: 8,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  infoLabel: {
    fontWeight: 'bold',
  },
  infoValue: {
    opacity: 0.7,
  },
});
